import React from "react";
import { Link } from "react-router-dom";

const STATUS_STYLES = {
  open: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  cancelled: "bg-gray-500/15 text-gray-400 border-gray-500/40",
  expired: "bg-amber-500/15 text-amber-300 border-amber-500/40",
};

/** Small status pill — shared across Wanted / Posted / Won cards. */
export const CommissionStatusBadge = ({ status }) => (
  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] || "bg-gray-700 text-gray-300 border-gray-600"}`}>
    {status}
  </span>
);

const fmtAuthor = (c) =>
  c.ec_anonymous || !c.ec_username
    ? <span className="italic">anonymous EP</span>
    : <span className="text-emerald-400">@{c.ec_username}</span>;

const fmtLoc = (c) =>
  c.location?.city || c.location?.country
  || (Number.isFinite(c.location?.lat)
        ? `${c.location.lat.toFixed(2)}, ${c.location.lng.toFixed(2)}`
        : "Custom point");

/**
 * WantedCard — single row in the "Wanted near me" feed. Renders the
 * accept / sign-up CTA based on auth state.
 */
export const WantedCard = ({ c, user, hotEpIds, accepting, onAccept, onSignUp }) => {
  const boosted = c.boosted_until && new Date(c.boosted_until) > new Date();
  return (
    <article className="bg-gray-900/70 border border-gray-800 rounded-lg p-4 flex flex-col sm:flex-row gap-3" data-testid={`wanted-card-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-white truncate">{c.title}</h3>
          <CommissionStatusBadge status={c.status} />
          {Number.isFinite(c._km) && (
            <span className="text-[10px] text-gray-500" data-testid={`wanted-distance-${c.id}`}>
              {c._km < 1 ? "<1" : Math.round(c._km)} km away
            </span>
          )}
          {c.bumped_at && (
            <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full border border-amber-500/40"
              title={`Bumped ${new Date(c.bumped_at).toLocaleString()}`}
              data-testid={`wanted-bumped-${c.id}`}>↑ bumped</span>
          )}
          {boosted && (
            <span className="text-[10px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded-full border border-cyan-500/40"
              title={`Boosted until ${new Date(c.boosted_until).toLocaleString()}`}
              data-testid={`wanted-boosted-${c.id}`}>⚡ boosted</span>
          )}
          {hotEpIds?.has(c.ec_user_id) && (
            <span className="text-[10px] bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded-full border border-rose-500/40"
              title="This Exec Producer has boosted 3+ offers this week — deliveries are in high demand"
              data-testid={`wanted-hot-${c.id}`}>🔥 hot</span>
          )}
        </div>
        {c.description && <p className="text-gray-400 text-xs mt-1 line-clamp-2">{c.description}</p>}
        <p className="text-gray-500 text-xs mt-1">
          by {fmtAuthor(c)} · {fmtLoc(c)}
        </p>
        <div className="flex items-center gap-3 text-[11px] mt-2">
          <span className="text-amber-300 font-semibold">+{(c.bounty_credits || 0).toFixed(2)} cr bounty</span>
          <span className="text-gray-500">{c.max_duration_seconds}s · {c.storage_days}d{c.featured ? " · featured" : ""}</span>
          <span className="text-gray-500">expires {new Date(c.expires_at).toLocaleDateString()} {new Date(c.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      </div>
      {user ? (
        <button
          onClick={onAccept}
          disabled={accepting === c.id}
          className="self-start sm:self-center bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-900 text-xs font-bold py-2 px-3 rounded transition-colors whitespace-nowrap"
          data-testid={`wanted-accept-btn-${c.id}`}
        >
          {accepting === c.id ? "Reserving…" : "Accept this commission"}
        </button>
      ) : (
        <button
          onClick={onSignUp}
          className="self-start sm:self-center bg-amber-500 hover:bg-amber-400 text-gray-900 text-xs font-bold py-2 px-3 rounded transition-colors whitespace-nowrap"
          data-testid={`wanted-signup-btn-${c.id}`}
          title="Sign up free to claim this commission"
        >
          Sign up to claim →
        </button>
      )}
    </article>
  );
};

/**
 * PostedCard — single row in the EP's "I posted" tab. Surfaces bid-up
 * + boost + cancel actions for open offers, and the winner/video link
 * for completed ones.
 */
export const PostedCard = ({ c, busyId, boostingId, onBump, onBoost, onCancel, onWatch }) => (
  <article className="bg-gray-900/70 border border-gray-800 rounded-lg p-4 flex flex-col sm:flex-row gap-3" data-testid={`posted-card-${c.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-semibold text-white truncate">{c.title}</h3>
        <CommissionStatusBadge status={c.status} />
      </div>
      <p className="text-gray-500 text-xs mt-1">
        {fmtLoc(c)} · escrow {(c.escrow_credits || 0).toFixed(2)} cr · bounty {(c.bounty_credits || 0).toFixed(2)} cr
      </p>
      <p className="text-gray-500 text-[10px] mt-1">
        Posted {new Date(c.created_at).toLocaleString()}
        {c.status === "open" && <> · expires {new Date(c.expires_at).toLocaleString()}</>}
        {c.status === "completed" && c.completed_at && <> · completed {new Date(c.completed_at).toLocaleString()}</>}
      </p>
      {c.status === "completed" && c.winner_username && (
        <p className="text-cyan-300 text-xs mt-1">
          Fulfilled by <Link to={`/${c.winner_username}`} className="hover:underline">@{c.winner_username}</Link>
          {c.winner_video_id && (
            <> · <button onClick={() => onWatch(c.winner_video_id)} className="text-cyan-400 hover:underline" data-testid={`posted-watch-${c.id}`}>Watch the video →</button></>
          )}
        </p>
      )}
    </div>
    {c.status === "open" && (
      <div className="flex flex-col gap-1.5 self-start sm:self-center">
        <button
          onClick={onBump}
          className="text-xs px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold whitespace-nowrap"
          data-testid={`posted-bump-btn-${c.id}`}
          title="Top up the bounty so producers see your offer first"
        >↑ Bid up</button>
        <button
          onClick={onBoost}
          disabled={boostingId === c.id}
          className="text-xs px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 text-white font-semibold whitespace-nowrap disabled:opacity-60"
          data-testid={`posted-boost-btn-${c.id}`}
          title="Top of feed for 1h, no bounty change · 1 credit"
        >{boostingId === c.id ? "⚡ Boosting…" : "⚡ Boost (1 cr · 1 h)"}</button>
        <button
          onClick={onCancel}
          disabled={busyId === c.id}
          className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 disabled:opacity-60 whitespace-nowrap"
          data-testid={`posted-cancel-btn-${c.id}`}
        >{busyId === c.id ? "Cancelling…" : "Cancel & refund"}</button>
      </div>
    )}
  </article>
);

/**
 * WonCard — single row in the producer's "My deliveries" tab.
 */
export const WonCard = ({ c, onWatch }) => (
  <article className="bg-gray-900/70 border border-gray-800 rounded-lg p-4" data-testid={`won-card-${c.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <h3 className="font-semibold text-white truncate">{c.title}</h3>
      <CommissionStatusBadge status={c.status} />
    </div>
    <p className="text-gray-500 text-xs mt-1">
      for {fmtAuthor(c)} · {fmtLoc(c)}
    </p>
    <div className="flex items-center gap-3 text-[11px] mt-2">
      <span className="text-amber-300 font-semibold">+{(c.bounty_credits || 0).toFixed(2)} cr earned</span>
      <span className="text-gray-500">delivered {c.completed_at ? new Date(c.completed_at).toLocaleString() : "—"}</span>
    </div>
    {c.winner_video_id && (
      <button
        onClick={() => onWatch(c.winner_video_id)}
        className="mt-2 text-cyan-400 hover:underline text-xs"
        data-testid={`won-watch-${c.id}`}
      >Watch your delivery →</button>
    )}
  </article>
);
